<template>
  <nav
    class="TabNav"
    :style="{
      '--activeItemIndex': activeItemIndex,
      '--itemCount': navItems.length,
    }"
  >
    <div class="TabNav__container">
      <ul class="TabNav__list">
        <li
          v-for="(navItem, index) in navItems"
          :key="navItem.label"
          class="TabNav__item"
        >
          <RouterLink
            class="TabNav__link"
            :to="navItem.route"
            :style="{
              '--itemIndex': index,
            }"
          >
            <span class="TabNav__linkLabel">{{ navItem.label }}</span>
            <span
              class="TabNav__linkLabel"
              aria-hidden="true"
            >{{ navItem.label }}</span>
          </RouterLink>
        </li>
      </ul>
      <div
        class="TabNav__indicator"
        aria-hidden="true"
      >
        <div class="TabNav__indicatorList">
          <div
            v-for="navItem in navItems"
            :key="navItem.label"
            class="TabNav__indicatorItem"
          >
            {{ navItem.label }}
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import { RouteName } from "@/lib/router/routes";
import { computed } from "vue";
import { RouteLocationAsRelative, useRouter } from "vue-router";

const {
  navItems,
} = defineProps<{
  navItems: Array<{
    label: string,
    route: RouteLocationAsRelative & { name: RouteName },
  }>,
}>();

const router = useRouter();

const activeItemIndex = computed(() => {
  return navItems.findIndex((item) => router.currentRoute.value.matched.some((match) => match.name === item.route.name));
});
</script>

<style lang="scss" scoped>
.TabNav {
  --transitionDuration: 250ms;
  padding: 6px 8px 12px;

  background-color: var(--themeColorPrimary);
  color: var(--themeColorPrimaryForeground);

  font-size: 14px;
  font-stretch: var(--fontWidthExtended);
}

.TabNav__container {
  position: relative;
}

.TabNav__list {
  position: relative;

  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;

  border-radius: 4px;
  background-color: var(--themeLightTransparent200);
}

.TabNav__item {
  position: relative;

  &:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  & + &::before {
    position: absolute;
    inset-block: 6px;

    border-left: 1px solid var(--themeLightTransparent200);

    transition: var(--transitionDuration) opacity;

    content: "";
  }

  &:last-child {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  &:has(.router-link-exact-active)::before,
  &:has(.router-link-exact-active) + &::before {
    opacity: 0;
  }
}

.TabNav__link {
  position: relative;

  display: grid;
  height: 28px;
  padding: 0 8px;
}

.TabNav__linkLabel {
  position: absolute;
  inset: 0;

  display: grid;
  place-items: center;
}

.TabNav__indicator {
  position: absolute;
  top: 0;
  left: 0;
  transform: translateX(calc(var(--activeItemIndex) * 100%));

  width: calc(1 / var(--itemCount) * 100%);
  height: 100%;
  overflow: hidden;

  border-radius: 4px;
  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  font-weight: var(--fontWeightSemibold);

  transition: var(--transitionDuration) transform;
  pointer-events: none;
}

.TabNav__indicatorList {
  position: relative;
  transform: translateX(calc(var(--activeItemIndex) * -100%));

  display: grid;
  grid-auto-columns: 100%;
  grid-auto-flow: column;
  place-items: center;
  height: 100%;

  text-align: center;

  transition: var(--transitionDuration) transform;
}
</style>
